import type { PromptDocument, UiPreference } from '@prompt-optimize/core'
import { createLogger } from '@prompt-optimize/shared'
import { getDb, type PreferenceRow, type PromptDatabase } from './db'
import { StorageError } from './errors'

const log = createLogger('storage')

const PREFERENCE_ID = 'singleton' as const

export class PromptRepository {
  private readonly db: PromptDatabase

  constructor(db: PromptDatabase = getDb()) {
    this.db = db
  }

  // ---------- documents ----------

  async listDocuments(): Promise<PromptDocument[]> {
    return this.db.documents.orderBy('updatedAt').reverse().toArray()
  }

  async listStarredDocuments(): Promise<PromptDocument[]> {
    return this.db.documents
      .where('starred')
      .equals(1) // IndexedDB stores booleans as 0/1 via Dexie
      .toArray()
      .then(rows => rows.sort((a, b) => b.updatedAt - a.updatedAt))
      .catch(async () => {
        // Fallback: filter in memory
        const all = await this.db.documents.toArray()
        return all.filter(d => d.starred).sort((a, b) => b.updatedAt - a.updatedAt)
      })
  }

  async getDocument(id: string): Promise<PromptDocument | undefined> {
    return this.db.documents.get(id)
  }

  async saveDocument(doc: PromptDocument): Promise<void> {
    if (!doc?.id)
      throw new StorageError('INVALID_INPUT', 'Document must have an id')
    const next: PromptDocument = { ...doc, updatedAt: Date.now() }
    await this.db.documents.put(next).catch((cause) => {
      log.error('saveDocument failed', cause)
      throw new StorageError('DB_FAILURE', 'Failed to save document', { cause })
    })
  }

  async toggleDocumentStar(id: string): Promise<boolean> {
    const doc = await this.db.documents.get(id)
    if (!doc)
      throw new StorageError('INVALID_INPUT', 'Document not found')
    const starred = !doc.starred
    await this.db.documents.update(id, { starred, updatedAt: Date.now() }).catch((cause) => {
      log.error('toggleDocumentStar failed', cause)
      throw new StorageError('DB_FAILURE', 'Failed to toggle star', { cause })
    })
    return starred
  }

  async deleteDocument(id: string): Promise<void> {
    await this.db.documents.delete(id).catch((cause) => {
      log.error('deleteDocument failed', cause)
      throw new StorageError('DB_FAILURE', 'Failed to delete document', { cause })
    })
  }

  // ---------- preferences ----------

  async getPreference(): Promise<UiPreference | undefined> {
    const row = await this.db.preferences.get(PREFERENCE_ID)
    if (!row)
      return undefined
    const { id: _id, updatedAt: _t, ...rest } = row
    return rest
  }

  async savePreference(pref: UiPreference): Promise<void> {
    const row: PreferenceRow = { ...pref, id: PREFERENCE_ID, updatedAt: Date.now() }
    await this.db.preferences.put(row).catch((cause) => {
      log.error('savePreference failed', cause)
      throw new StorageError('DB_FAILURE', 'Failed to save preference', { cause })
    })
  }
}

/**
 * Default shared repository for the app. Multi-tab safe (IndexedDB is shared
 * across tabs; Dexie uses its own connection per tab).
 */
export const promptRepository = new PromptRepository()
